import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useProgramSetup } from "@/lib/programSetup";

const DISMISS_STORAGE_KEY = "campdesk-program-setup-dismissed";

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === "string");
  } catch {
    return [];
  }
}

export function ProgramSetupPanel() {
  const { setup, panelOpen, closePanel } = useProgramSetup();
  const [dismissed, setDismissed] = useState<string[]>(() => readDismissed());
  const [showDismissed, setShowDismissed] = useState(false);

  useEffect(() => {
    if (!setup) return;
    const openCodes = new Set(setup.steps.map((step) => step.code));
    setDismissed((current) => {
      const next = current.filter((code) => openCodes.has(code));
      if (next.length === current.length) return current;
      localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, [setup]);

  const dismissedSet = useMemo(() => new Set(dismissed), [dismissed]);
  const openSteps = setup?.steps ?? [];
  const hiddenRecommended = openSteps.filter(
    (step) => step.priority === "recommended" && dismissedSet.has(step.code),
  );
  const visibleSteps = openSteps.filter(
    (step) => step.priority === "required" || showDismissed || !dismissedSet.has(step.code),
  );
  const requiredVisible = visibleSteps.filter((step) => step.priority === "required").length;
  const progress =
    setup && setup.total_steps > 0 ? Math.round((setup.completed_count / setup.total_steps) * 100) : 0;
  const summary =
    !setup || openSteps.length === 0
      ? "Alle Schritte sind erledigt. Die Rezeption kann vollständig genutzt werden."
      : requiredVisible > 0
        ? "Diese Schritte fehlen noch, bevor Sie das Programm vollständig nutzen können."
        : "Die Pflichtangaben sind hinterlegt. Diese Punkte machen den Betrieb vollständig.";

  function dismiss(code: string) {
    setDismissed((current) => {
      if (current.includes(code)) return current;
      const next = [...current, code];
      localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }

  return (
    <Transition show={panelOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closePanel}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim/60" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 flex justify-end">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-250"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="flex h-full w-full max-w-md flex-col bg-card shadow-ios-lg">
              <header className="shrink-0 border-b border-separator px-4 py-4 sm:px-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <DialogTitle className="text-base font-semibold text-label">Einrichtung</DialogTitle>
                    <p className="mt-1 text-sm text-label-secondary">{summary}</p>
                  </div>
                  <button
                    type="button"
                    onClick={closePanel}
                    className="rounded-lg p-2 text-label-secondary hover:bg-fill hover:text-label"
                    aria-label="Einrichtung schließen"
                  >
                    <X size={18} aria-hidden />
                  </button>
                </div>
                {setup && setup.total_steps > 0 ? (
                  <>
                    <p className="mt-3 text-sm font-medium text-label">
                      {setup.completed_count} von {setup.total_steps} erledigt
                    </p>
                    <div
                      className="mt-2 h-1.5 overflow-hidden rounded-full bg-fill-tertiary"
                      role="progressbar"
                      aria-valuenow={setup.completed_count}
                      aria-valuemin={0}
                      aria-valuemax={setup.total_steps}
                      aria-label="Fortschritt der Einrichtung"
                    >
                      <div className="h-full rounded-full bg-accent" style={{ width: `${progress}%` }} />
                    </div>
                  </>
                ) : null}
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-4">
                {!setup ? (
                  <p className="px-1 py-6 text-sm text-label-secondary">Einrichtung wird geladen…</p>
                ) : openSteps.length === 0 ? (
                  <p className="px-1 py-6 text-sm text-label-secondary">
                    Alles erledigt. Sie können dieses Fenster schließen.
                  </p>
                ) : visibleSteps.length === 0 ? (
                  <div className="rounded-xl border border-separator px-4 py-3">
                    <p className="text-sm text-label-secondary">
                      {hiddenRecommended.length === 1
                        ? "1 empfohlener Einrichtungsschritt ist ausgeblendet."
                        : `${hiddenRecommended.length} empfohlene Einrichtungsschritte sind ausgeblendet.`}
                    </p>
                    <button
                      type="button"
                      className="mt-2 text-sm font-medium text-accent hover:underline"
                      onClick={() => setShowDismissed(true)}
                    >
                      Anzeigen
                    </button>
                  </div>
                ) : (
                  <ol className="space-y-2">
                    {visibleSteps.map((step) => (
                      <li key={step.code} className="rounded-xl border border-separator px-3 py-3 sm:px-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={
                              step.priority === "required"
                                ? "rounded-full bg-status-red/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-status-red"
                                : "rounded-full bg-status-orange/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-status-orange"
                            }
                          >
                            {step.priority === "required" ? "Erforderlich" : "Empfohlen"}
                          </span>
                          <p className="text-sm font-semibold text-label">{step.title}</p>
                        </div>
                        <p className="mt-1 text-sm text-label-secondary">{step.detail}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <Link
                            to={step.action_path}
                            onClick={closePanel}
                            className="text-sm font-medium text-accent hover:underline"
                          >
                            {step.action_label}
                          </Link>
                          {step.priority === "recommended" ? (
                            <button
                              type="button"
                              className="text-sm text-label-secondary hover:text-label hover:underline"
                              onClick={() => {
                                dismiss(step.code);
                                setShowDismissed(false);
                              }}
                            >
                              Später
                            </button>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {hiddenRecommended.length > 0 && !showDismissed && visibleSteps.length > 0 ? (
                <div className="shrink-0 border-t border-separator px-4 py-3 sm:px-5">
                  <button
                    type="button"
                    className="text-sm text-label-secondary hover:text-label hover:underline"
                    onClick={() => setShowDismissed(true)}
                  >
                    {hiddenRecommended.length === 1
                      ? "1 ausgeblendeten Hinweis anzeigen"
                      : `${hiddenRecommended.length} ausgeblendete Hinweise anzeigen`}
                  </button>
                </div>
              ) : null}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
